import { Button } from "$components/button/button";
import { CheckIcon } from "$components/icons";
import { Page } from "$components/page";
import { toast } from "$global-providers/toast-provider";
import { useUser } from "$global-providers/user-provider";
import InventoryTitleRow from "$routes/inventory/_lib/inventory-title-row";
import { RHFLocationSelect } from "$routes/inventory/_lib/location-select";
import { linkToInventoryMoveRequests } from "$routes/inventory/links";
import useUiDefault from "$routes/user/_lib/use-ui-default";
import { gql } from "@apollo/client";
import { yupResolver } from "@hookform/resolvers/yup";
import { styled } from "@material-ui/core";
import { DefaultScope as UiDefaultScope } from "elephant-web/gql";
import React, { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { FormattedMessage, useIntl } from "react-intl";
import { useNavigate } from "react-router-dom";
import { useRequestInventoryMoveMutation } from "./__generated__/page.gql";
import SkuRequestTable from "./_lib/sku-request-table";
import { type FormValues, validationSchema } from "./_lib/validation";

const SubmitButton = styled(Button)(({ theme }) => ({
  marginTop: theme.spacing(2),
}));

gql`
  mutation requestInventoryMove($input: RequestInventoryMoveInput!) {
    requestInventoryMove(input: $input) {
      # Don't bother caching any fields like requestor, as we're redirected to a page that has to fetch a list of requests anyway.
      id
    }
  }
`;

interface LocationIdUiDefault {
  locationId?: string;
}

const MoveRequestNew: React.FunctionComponent<unknown> = () => {
  const {
    value: { locationId: defaultLocationId },
    save: saveUiDefault,
  } = useUiDefault<LocationIdUiDefault>({
    scope: UiDefaultScope.User,
    namespace: "elephant.inventory.moveRequestNew",
    name: "locationId",
    defaultValue: {},
  });

  const formMethods = useForm<FormValues>({
    defaultValues: { skus: [], locationId: defaultLocationId },
    resolver: yupResolver(validationSchema),
    mode: "onTouched",
  });

  useEffect(() => {
    if (!formMethods.getValues("locationId")) {
      formMethods.setValue("locationId", defaultLocationId);
    }
  }, [defaultLocationId, formMethods]);

  const { facilityId } = useUser();
  const navigate = useNavigate();
  const { formatMessage } = useIntl();

  const [requestInventoryMove] = useRequestInventoryMoveMutation({
    onError: (error) => toast.error(error.message),
    onCompleted: () =>
      toast.success(
        formatMessage({
          id: "inventory.moveRequest.new.notification.submit.success",
          defaultMessage: "Request Submitted",
        }),
      ),
  });

  const submit: Parameters<typeof formMethods.handleSubmit>[0] = async (
    data: FormValues,
  ) => {
    saveUiDefault({ locationId: data.locationId });
    await requestInventoryMove({
      variables: {
        input: {
          receivingFacilityId: facilityId,
          receivingLocationId: data.locationId,
          skus: data.skus.map((sku) => ({
            skuId: sku.sku.id,
            quantityRequested: sku.quantity,
          })),
        },
      },
    });
    navigate(linkToInventoryMoveRequests());
  };

  const skus = formMethods.watch("skus");
  const someSkusFilledIn = skus.some(({ quantity }) => quantity > 0);

  return (
    <form onSubmit={formMethods.handleSubmit(submit)}>
      <FormProvider {...formMethods}>
        <Page data-dense>
          <InventoryTitleRow
            title={
              <FormattedMessage
                id="inventory.request.new.title"
                defaultMessage="New Move Request"
              />
            }
          >
            <div style={{ width: "250px" }}>
              <RHFLocationSelect id="locationId" label="Receiving Location" />
            </div>
          </InventoryTitleRow>
          <SkuRequestTable />
          <SubmitButton
            color="primary"
            variant="contained"
            type="submit"
            startIcon={<CheckIcon color="white" />}
            disabled={!someSkusFilledIn}
          >
            <FormattedMessage
              id="inventory.moveRequest.new.header.submit"
              defaultMessage="Submit Request"
            />
          </SubmitButton>
        </Page>
      </FormProvider>
    </form>
  );
};

export default MoveRequestNew;
